import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Input } from "./ui/input";
import { Search } from "lucide-react";

/**
 * ClientAutocomplete: dropdown with previously saved clients.
 * Triggered by typing in the field; calls onPick(client) when one is selected.
 */
export const ClientAutocomplete = ({ value, onChange, onPick, placeholder, testid }) => {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Debounced search on value change while open
  useEffect(() => {
    if (!open) return undefined;
    const q = (value || "").trim();
    const t = setTimeout(async () => {
      try {
        const res = await api.get(`/clients`, { params: q ? { q } : {} });
        setResults(res.data || []);
      } catch {
        setResults([]);
      }
    }, 150);
    return () => clearTimeout(t);
  }, [value, open]);

  const handlePick = (client) => {
    onPick(client);
    setOpen(false);
    setActiveIdx(-1);
  };

  const handleKeyDown = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && activeIdx >= 0 && results[activeIdx]) {
      e.preventDefault();
      handlePick(results[activeIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      <Input
        ref={inputRef}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="mt-1 rounded-sm border-zinc-300 focus-visible:ring-black"
        data-testid={testid}
        autoComplete="off"
      />
      {open && results.length > 0 && (
        <div
          className="absolute z-30 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-zinc-300 rounded-sm shadow-lg"
          data-testid={`${testid}-dropdown`}
        >
          {results.map((c, i) => {
            const fullname = `${c.last_name || ""} ${c.first_name || ""}`.trim() || "—";
            const subtitle = [c.cui, c.cnp, c.phone].filter(Boolean).join(" · ");
            return (
              <button
                key={c.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handlePick(c)}
                className={`w-full text-left px-3 py-2 text-sm border-b border-zinc-100 last:border-b-0 transition ${
                  i === activeIdx ? "bg-zinc-100" : "hover:bg-zinc-50"
                }`}
                data-testid={`${testid}-option-${c.id}`}
              >
                <div className="text-black font-medium flex items-center gap-2">
                  <Search size={11} className="text-zinc-400" />
                  {fullname}
                </div>
                {subtitle && (
                  <div className="text-xs text-zinc-500 font-mono mt-0.5 ml-5">{subtitle}</div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
